import type { MatchPerson } from '@uefadigital/panenka';
import { MatchPersonRole } from '@uefadigital/panenka';
import { PkAvatar, PkIdentifier, PkListItem } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useRefereeOrder } from 'hooks/useRefereeOrder';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

export interface MatchOfficialsData {
  isDesktop: boolean;
  isMatchOfficialsTab?: boolean;
  refereeData: Array<MatchPerson> | undefined;
  isPDFPrint?: boolean;
  isFemale?: boolean;
}

const cssPropertiesPrimary: CSSProperties = {
  fontWeight: 'var(--pk-font-weight-m)',
  fontFamily: 'var(--pk-font-base-regular)',
};
const cssPropertiesSecondary: CSSProperties = {
  fontWeight: 'var(--pk-font-weight-l)',
  fontFamily: 'var(--pk-font-base-semibold)',
};

export const MatchOfficials = ({
  isDesktop,
  isMatchOfficialsTab = false,
  refereeData = [],
  isPDFPrint = false,
  isFemale,
}: MatchOfficialsData) => {
  const { t } = useTranslation();
  const newRefereesArr = useRefereeOrder(refereeData);

  const Title = isDesktop
    ? 'pk-font-size--xl2 pk-font-weight--bolder pk-line-height--sd pk-font-style--normal pk-mt--xl2 pk-mb--m'
    : 'pk-font-size--xl pk-font-weight--bolder pk-line-height--sd pk-font-style--normal  pk-mb--l';
  const isMatchOfficialsTabHeading = !isMatchOfficialsTab
    ? 'pk-font-size--xl pk-font-weight--bolder pk-line-height--sd pk-font-style--normal pk-mb--l'
    : Title;

  const cssForPrint = 'pk-font-size--xl2 pk-font-weight--bolder pk-line-height--sd pk-font-style--normal';

  return (
    <>
      <div
        className={clsx(
          isPDFPrint ? cssForPrint : isMatchOfficialsTabHeading,
          'pk-m--0 pk-text--text-01 pr-font-size--18 pk-font--comp-regular',
        )}
      >
        {t('match_officials')}
      </div>
      {(isMatchOfficialsTab && isDesktop) || isPDFPrint ? (
        <div className="pk-py--xl">
          <div className={clsx(style.matchOfficialsGrid, 'pr-display--none pr-display--flex pk-d--grid pk-gap--l')}>
            {newRefereesArr.map((referee, key: number) => {
              if (referee?.role !== MatchPersonRole.ASSISTANT_REFEREE_TWO) {
                return (
                  <div
                    key={key}
                    className="pk-d--flex pk-justify-content--start pk-flex--column pr-width--31p  pr-avoid-break--inside"
                  >
                    <p className="pk-font-size--l pk-text--text-01 pk-line-height--md pk-mb--m pr-font-size--14 pk-font--comp-bold">
                      {referee?.role === MatchPersonRole.ASSISTANT_VIDEO_ASSISTANT_REFEREE_ONE
                        ? t(`assistant_video_assistant_referees${isFemale ? '_f' : ''}`)
                        : translateEntity(referee)?.roleName}
                    </p>
                    <PkListItem bg-color="elevation-01" has-divider="false">
                      {referee?.person &&
                        referee.person.map((person, i) => (
                          <PkIdentifier className="pk-py--xs2" key={person?.id}>
                            <span
                              slot="prefix"
                              className="pk-mr--s"
                              style={
                                {
                                  opacity: referee?.images?.SMALL_SQUARE ? 100 : 0,
                                  minHeight: key < 3 && i === 0 ? '52px' : null,
                                } as CSSProperties
                              }
                            >
                              {key === 0 ? (
                                <PkAvatar
                                  fallback-image="generic-head"
                                  size={40}
                                  src={referee?.images?.SMALL_SQUARE}
                                  type="image"
                                  className="pr-avatar-size--30"
                                />
                              ) : null}
                            </span>
                            <div />
                            <span
                              slot="secondary"
                              style={cssPropertiesSecondary}
                              className="pk-font-size--m pk-line-height--lg pk-text--text-01 pr-font-size--12"
                            >
                              {translateEntity(person)?.name}
                            </span>
                            <span
                              slot="secondary"
                              className="pk-font-size--m pk-line-height--lg pk-text--ui-04 pr-font-size--12"
                            >
                              {' '}
                              {person?.countryCode}
                            </span>
                          </PkIdentifier>
                        ))}
                    </PkListItem>
                  </div>
                );
              }
            })}
          </div>
        </div>
      ) : (
        <div className="pk-d--flex pk-flex--column pr-display--none">
          {newRefereesArr.map((referee, key: number) => (
            <PkListItem bg-color="elevation-01" has-divider="false" key={key}>
              {referee?.person &&
                referee.person.map((person, idx) => (
                  <PkIdentifier className={clsx('pk-pb--xs2', idx === 0 && 'pk-pt--xs2')} key={person?.id}>
                    <span
                      slot="prefix"
                      style={{
                        opacity: referee?.images?.SMALL_SQUARE ? 100 : 0,
                        marginLeft: key === 0 ? 0 : 41,
                      }}
                    >
                      {key === 0 ? (
                        <PkAvatar
                          fallback-image="generic-head"
                          size={40}
                          src={referee?.images?.SMALL_SQUARE}
                          type="image"
                        />
                      ) : null}
                    </span>
                    {idx === 0 && (
                      <span slot="primary" className="pk-text--ui-04 pk-text--wrap" style={cssPropertiesPrimary}>
                        {referee?.role === MatchPersonRole.ASSISTANT_VIDEO_ASSISTANT_REFEREE_ONE
                          ? t(`assistant_video_assistant_referees${isFemale ? '_f' : ''}`)
                          : translateEntity(referee)?.roleName}
                      </span>
                    )}
                    <span slot="secondary" style={cssPropertiesSecondary} className="pk-font-size--s pk-text--text-01">
                      {translateEntity(person)?.name}
                    </span>
                    <span slot="secondary" className="pk-font-size--s pk-text--ui-04">
                      {' '}
                      {person?.countryCode}
                    </span>
                  </PkIdentifier>
                ))}
            </PkListItem>
          ))}
        </div>
      )}
    </>
  );
};
